import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '@/api/client'
import type { EmployeeAdminRow, PortalRole, WageClassificationRow } from '@/api/types'

const ROLES: PortalRole[] = ['Technician', 'Supervisor', 'Office', 'Admin']

type SortKey = 'name' | 'employee_no' | 'job_title' | 'role' | 'supervisor'

interface SortState {
  by: SortKey
  dir: 'asc' | 'desc'
}

/** Clickable column header. Clicking the active column reverses direction. */
function SortHeader({
  label,
  col,
  sort,
  onSort,
}: {
  label: string
  col: SortKey
  sort: SortState
  onSort: (s: SortState) => void
}) {
  const active = sort.by === col
  return (
    <th
      role="button"
      tabIndex={0}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() =>
        onSort({ by: col, dir: active && sort.dir === 'asc' ? 'desc' : 'asc' })
      }
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSort({ by: col, dir: active && sort.dir === 'asc' ? 'desc' : 'asc' })
        }
      }}
    >
      {label}
      <span aria-hidden style={{ marginLeft: 4, opacity: active ? 1 : 0.25 }}>
        {active && sort.dir === 'desc' ? '▾' : '▴'}
      </span>
    </th>
  )
}

/**
 * Employee roster.
 *
 * Sage knows who exists but not what they are called — PR_Employee returns
 * empty names for every one of the 185 active employees because Sage protects
 * PII at rest. This screen is where a human supplies the missing names once.
 *
 * Everyone in Sage is listed, including people with no portal record yet.
 * That subset IS the setup work queue, so it is surfaced rather than hidden.
 */
export function EmployeesPage() {
  const [rows, setRows] = useState<EmployeeAdminRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [dept, setDept] = useState('21')
  const [filter, setFilter] = useState('')
  const [supervisorsOnly, setSupervisorsOnly] = useState(false)
  const [sort, setSort] = useState<{ by: SortKey; dir: 'asc' | 'desc' }>({
    by: 'name',
    dir: 'asc',
  })
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState({
    first: '',
    last: '',
    role: 'Technician' as PortalRole,
    wageClassId: 0,
    // Where they live. Sage cannot supply any of this — the address block is
    // app-encrypted and empty for everyone — and it is what per diem
    // eligibility is judged on.
    resCity: '',
    resState: '',
    resZip: '',
    resNote: '',
  })
  const [pinFor, setPinFor] = useState<string | null>(null)
  const [pin, setPin] = useState('')
  const [classes, setClasses] = useState<WageClassificationRow[]>([])
  // Bulk selection: "these four are foremen" is one action, not four edits.
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkClassId, setBulkClassId] = useState<number>(0)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [r, c] = await Promise.all([
        api.adminEmployees(dept || undefined),
        api.wageClassifications(),
      ])
      setRows(r)
      setClasses(c)
      if (c.length && !bulkClassId) setBulkClassId(c[0].wage_classification_id)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load employees')
    }
    // bulkClassId deliberately omitted: including it would refetch the whole
    // roster every time the bulk dropdown changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dept])

  async function applyBulkClassification() {
    if (!selected.size || !bulkClassId) return
    setBusy(true)
    setError(null)
    try {
      await api.adminSetClassification([...selected], bulkClassId)
      const name = classes.find((c) => c.wage_classification_id === bulkClassId)?.description
      setSelected(new Set())
      await load()
      setNotice(`${selected.size} moved to ${name}.`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reclassify')
    } finally {
      setBusy(false)
    }
  }

  function toggleSelected(key: string) {
    const next = new Set(selected)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    setSelected(next)
  }

  useEffect(() => {
    void load()
  }, [load])

  const visible = useMemo(() => {
    if (!rows) return []
    let list = rows
    if (supervisorsOnly) list = list.filter((r) => r.looks_like_supervisor)

    const q = filter.trim().toLowerCase()
    if (q) {
      list = list.filter(
        (r) =>
          r.employee_no.toLowerCase().includes(q) ||
          (r.job_title ?? '').toLowerCase().includes(q) ||
          (r.location ?? '').toLowerCase().includes(q) ||
          (r.badge_no ?? '').toLowerCase().includes(q) ||
          `${r.first_name ?? ''} ${r.last_name ?? ''}`.toLowerCase().includes(q),
      )
    }

    const key = (r: EmployeeAdminRow): string => {
      switch (sort.by) {
        case 'employee_no':
          return r.employee_no
        case 'job_title':
          return (r.job_title ?? '￿').toLowerCase()
        case 'role':
          return (r.portal_role ?? '￿').toLowerCase()
        case 'supervisor':
          return (r.supervisor_name ?? '￿').toLowerCase()
        default:
          // Unnamed employees sort last regardless of direction — they are the
          // outstanding setup work, not a name that happens to start with Z.
          return r.has_name
            ? `0${r.last_name ?? ''} ${r.first_name ?? ''}`.toLowerCase()
            : `1${r.employee_no}`
      }
    }

    return [...list].sort((a, b) => {
      const cmp = key(a).localeCompare(key(b))
      return sort.dir === 'asc' ? cmp : -cmp
    })
  }, [rows, filter, supervisorsOnly, sort])

  const missingNames = rows?.filter((r) => !r.has_name).length ?? 0
  const likelySupervisors = rows?.filter((r) => r.looks_like_supervisor).length ?? 0

  function startEdit(r: EmployeeAdminRow) {
    setEditing(r.sage_employee_key)
    setDraft({
      first: r.first_name ?? '',
      last: r.last_name ?? '',
      role: (r.portal_role as PortalRole) ?? 'Technician',
      wageClassId: r.wage_classification_id ?? 0,
      resCity: r.residence_city ?? '',
      resState: r.residence_state ?? '',
      resZip: r.residence_zip ?? '',
      resNote: r.residence_note ?? '',
    })
    setNotice(null)
  }

  async function save(key: string) {
    setError(null)
    if (!draft.first.trim() || !draft.last.trim()) {
      return setError('First and last name are both required.')
    }
    try {
      await api.adminUpsertEmployee(key, {
        first_name: draft.first,
        last_name: draft.last,
        portal_role: draft.role,
        is_active: true,
        wage_classification_id: draft.wageClassId || null,
        // Sent even when blank: unlike the classification these are cleared by
        // emptying them, and a residence note that turned out to be wrong has
        // to be removable.
        residence_city: draft.resCity.trim() || null,
        residence_state: draft.resState.trim().toUpperCase() || null,
        residence_zip: draft.resZip.trim() || null,
        residence_note: draft.resNote.trim() || null,
      })
      setEditing(null)
      setNotice(`Saved ${draft.first} ${draft.last}`)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save')
    }
  }

  async function savePin(key: string) {
    setError(null)
    if (!/^\d{4,12}$/.test(pin)) return setError('PIN must be 4–12 digits.')
    try {
      await api.adminSetPin(key, pin)
      setPinFor(null)
      setPin('')
      setNotice('PIN set.')
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not set PIN')
    }
  }

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      <div className="card">
        <h2>Employees</h2>
        <p className="muted">
          Sage encrypts employee names, so they are entered here once. Everyone
          active in Sage is listed — <strong>job title and location come
          straight from Sage</strong>, so you can tell who is who before any
          names exist.
        </p>
        {missingNames > 0 && (
          <div className="alert alert-warn">
            <strong>{missingNames}</strong> employees in this department have no
            name yet. They cannot be picked for a crew until they do.
          </div>
        )}
        {likelySupervisors > 0 && (
          <div className="alert alert-info">
            <strong>{likelySupervisors}</strong> job titles look like
            supervisors or foremen. Start with those — set their role to
            Supervisor so crews can be built around them.
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label htmlFor="dept">Department</label>
            <select id="dept" value={dept} onChange={(e) => setDept(e.target.value)}>
              <option value="21">21 — Construction Field Staff</option>
              <option value="01">01 — Construction Administration</option>
              <option value="00">00 — Administrative &amp; Accounting</option>
              <option value="14">14 — Warehouse</option>
              <option value="04">04 — Sales</option>
              <option value="">All departments</option>
            </select>
          </div>
          <div className="form-field" style={{ marginBottom: 0, flex: 1, minWidth: 180 }}>
            <label htmlFor="filter">Search</label>
            <input
              id="filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Number, name, job title or location"
            />
          </div>
          <div className="form-field" style={{ marginBottom: 0, justifyContent: 'flex-end' }}>
            <button
              className={`btn ${supervisorsOnly ? '' : 'btn-secondary'}`}
              aria-pressed={supervisorsOnly}
              onClick={() => setSupervisorsOnly((v) => !v)}
            >
              Supervisors only
            </button>
          </div>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="card" style={{ borderColor: 'var(--c-brand)' }}>
          <div className="crew-heading">
            <h2 className="title" style={{ margin: 0 }}>
              {selected.size} selected
            </h2>
            <button className="btn btn-secondary" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
          <p className="muted">
            Wage classification decides which prevailing wage rate applies to
            someone on a job. Everyone defaults to Field Laborer, so the few who
            earn a foreman or operator rate need moving here.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-field" style={{ marginBottom: 0, minWidth: 220 }}>
              <label htmlFor="bulkCls">Set wage classification to</label>
              <select
                id="bulkCls"
                value={bulkClassId}
                onChange={(e) => setBulkClassId(Number(e.target.value))}
              >
                {classes.map((c) => (
                  <option key={c.wage_classification_id} value={c.wage_classification_id}>
                    {c.description}
                    {c.employee_count ? ` — ${c.employee_count} now` : ''}
                  </option>
                ))}
              </select>
            </div>
            <button className="btn" disabled={busy} onClick={() => void applyBulkClassification()}>
              {busy ? 'Applying…' : `Apply to ${selected.size}`}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        {rows === null ? (
          <p className="muted">Loading…</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      aria-label="Select all shown"
                      style={{ minHeight: 'auto' }}
                      checked={
                        visible.length > 0 &&
                        visible.every((r) => selected.has(r.sage_employee_key))
                      }
                      onChange={(e) =>
                        setSelected(
                          e.target.checked
                            ? new Set(visible.map((r) => r.sage_employee_key))
                            : new Set(),
                        )
                      }
                    />
                  </th>
                  <SortHeader label="Employee" col="employee_no" sort={sort} onSort={setSort} />
                  <SortHeader label="Job title (from Sage)" col="job_title" sort={sort} onSort={setSort} />
                  <th>Location</th>
                  <SortHeader label="Name" col="name" sort={sort} onSort={setSort} />
                  <SortHeader label="Role" col="role" sort={sort} onSort={setSort} />
                  <th>Wage class</th>
                  {/* Not from Sage. This is the workbook's "State of Res." —
                      the basis on which per diem eligibility is decided. */}
                  <th>Residence</th>
                  <SortHeader label="Supervisor" col="supervisor" sort={sort} onSort={setSort} />
                  <th>PIN</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const isEditing = editing === r.sage_employee_key
                  const isPinning = pinFor === r.sage_employee_key
                  return (
                    <tr key={r.sage_employee_key}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Select ${r.employee_no}`}
                          style={{ minHeight: 'auto' }}
                          disabled={!r.has_portal_record}
                          checked={selected.has(r.sage_employee_key)}
                          onChange={() => toggleSelected(r.sage_employee_key)}
                        />
                      </td>
                      <td className="mono">
                        {r.employee_no}
                        {r.badge_no && (
                          <div className="muted" style={{ fontSize: '0.75rem' }}>
                            badge {r.badge_no}
                          </div>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'normal', maxWidth: 220 }}>
                        {r.job_title ?? <span className="muted">—</span>}
                        {r.looks_like_supervisor && (
                          <span className="badge badge-info" style={{ marginLeft: 6 }}>
                            Supervisor?
                          </span>
                        )}
                      </td>
                      <td className="muted">{r.location ?? '—'}</td>
                      <td>
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <input
                              value={draft.first}
                              onChange={(e) => setDraft({ ...draft, first: e.target.value })}
                              placeholder="First"
                              style={{ width: 110 }}
                              autoFocus
                            />
                            <input
                              value={draft.last}
                              onChange={(e) => setDraft({ ...draft, last: e.target.value })}
                              placeholder="Last"
                              style={{ width: 130 }}
                            />
                          </div>
                        ) : r.has_name ? (
                          `${r.first_name} ${r.last_name}`
                        ) : (
                          <span className="badge badge-warn">No name</span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <select
                            value={draft.role}
                            onChange={(e) =>
                              setDraft({ ...draft, role: e.target.value as PortalRole })
                            }
                          >
                            {ROLES.map((role) => (
                              <option key={role} value={role}>
                                {role}
                              </option>
                            ))}
                          </select>
                        ) : (
                          r.portal_role ?? '—'
                        )}
                      </td>
                      {/* Drives which prevailing wage rate resolves for this
                          person. Unset means a job rate silently will not
                          apply, so it is called out rather than left blank. */}
                      <td>
                        {isEditing ? (
                          <select
                            value={draft.wageClassId}
                            onChange={(e) =>
                              setDraft({ ...draft, wageClassId: Number(e.target.value) })
                            }
                            aria-label="Wage classification"
                          >
                            <option value={0}>— none —</option>
                            {classes.map((c) => (
                              <option key={c.wage_classification_id} value={c.wage_classification_id}>
                                {c.description}
                              </option>
                            ))}
                          </select>
                        ) : r.wage_classification_description ? (
                          r.wage_classification_description
                        ) : r.has_portal_record ? (
                          <span className="badge badge-warn">not set</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      {/* Reads back the way the workbook writes it:
                          WA (Kent), or WA (Othello) with the note beneath. */}
                      <td>
                        {isEditing ? (
                          <div style={{ display: 'grid', gap: 4, minWidth: 190 }}>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <input
                                value={draft.resCity}
                                onChange={(e) => setDraft({ ...draft, resCity: e.target.value })}
                                placeholder="City"
                                aria-label="Residence city"
                                maxLength={60}
                                style={{ flex: 2, minWidth: 0 }}
                              />
                              <input
                                value={draft.resState}
                                onChange={(e) =>
                                  setDraft({ ...draft, resState: e.target.value.toUpperCase() })
                                }
                                placeholder="ST"
                                aria-label="Residence state"
                                maxLength={2}
                                style={{ flex: '0 0 3.5rem', textTransform: 'uppercase' }}
                              />
                              <input
                                value={draft.resZip}
                                onChange={(e) => setDraft({ ...draft, resZip: e.target.value })}
                                placeholder="Zip"
                                aria-label="Residence zip"
                                maxLength={10}
                                style={{ flex: 1, minWidth: 0 }}
                              />
                            </div>
                            <input
                              value={draft.resNote}
                              onChange={(e) => setDraft({ ...draft, resNote: e.target.value })}
                              placeholder="e.g. over 50 from Kent"
                              aria-label="Residence note"
                              maxLength={200}
                            />
                          </div>
                        ) : r.residence_state || r.residence_city ? (
                          <>
                            {r.residence_state ?? ''}
                            {r.residence_city ? ` (${r.residence_city})` : ''}
                            {r.residence_note && (
                              <div className="muted" style={{ fontSize: '0.75rem' }}>
                                {r.residence_note}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="muted">{r.supervisor_name ?? '—'}</td>
                      <td>
                        {isPinning ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <input
                              value={pin}
                              onChange={(e) => setPin(e.target.value)}
                              inputMode="numeric"
                              placeholder="4–12 digits"
                              style={{ width: 110 }}
                              autoFocus
                            />
                            <button
                              className="btn"
                              style={{ minHeight: 36 }}
                              onClick={() => void savePin(r.sage_employee_key)}
                            >
                              Set
                            </button>
                          </div>
                        ) : r.has_pin ? (
                          <span className="badge badge-ok">Set</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              className="btn"
                              style={{ minHeight: 36 }}
                              onClick={() => void save(r.sage_employee_key)}
                            >
                              Save
                            </button>
                            <button
                              className="btn btn-secondary"
                              style={{ minHeight: 36 }}
                              onClick={() => setEditing(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              className="btn btn-secondary"
                              style={{ minHeight: 36 }}
                              onClick={() => startEdit(r)}
                            >
                              {r.has_name ? 'Edit' : 'Add name'}
                            </button>
                            {r.has_portal_record && (
                              <button
                                className="btn btn-secondary"
                                style={{ minHeight: 36 }}
                                onClick={() => {
                                  setPinFor(r.sage_employee_key)
                                  setPin('')
                                }}
                              >
                                PIN
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {visible.length === 0 && <p className="muted">No matching employees.</p>}
          </div>
        )}
      </div>
    </div>
  )
}
